import { ArchivalEntityType } from 'enums';
import { FavouriteArchivalEntity, Params, Result } from 'types';
import fetch from 'utils/fetch';

/**
 * Function for adding selected archival aid to favourites.
 *
 * @param id
 */
export async function addArchivalAidToFavourites(id: string) {
  try {
    await fetch(`/api/archival-aid-metadata/${id}/add-to-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for removing selected archival aid from favourites.
 *
 * @param id
 */
export async function removeArchivalAidFromFavourites(id: string) {
  try {
    await fetch(`/api/archival-aid-metadata/${id}/remove-from-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Adds ead node to favourites.
 *
 * @param id
 */
export async function addEadToFavourites(id: string) {
  try {
    await fetch(`/api/archival-aid-metadata/ead/${id}/add-to-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Removes ead node from favourites.
 *
 * @param id
 */
export async function removeEadFromFavourites(id: string) {
  try {
    await fetch(`/api/archival-aid-metadata/ead/${id}/remove-from-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for adding selected archival collection to favourites.
 *
 * @param id
 */
export async function addArchivalCollectionToFavourites(id: string) {
  try {
    await fetch(`/api/archival-collection/${id}/add-to-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for removing selected archival collection from favourites.
 *
 * @param id
 */
export async function removeArchivalCollectionFromFavourites(id: string) {
  try {
    await fetch(`/api/archival-collection/${id}/remove-from-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for adding selected archival unit to favourites.
 *
 * @param id
 */
export async function addArchivalUnitToFavourites(id: string) {
  try {
    await fetch(`/api/archival-unit/public/${id}/add-to-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for removing selected archival unit from favourites.
 *
 * @param id
 */
export async function removeArchivalUnitFromFavourites(id: string) {
  try {
    await fetch(`/api/archival-unit/public/${id}/remove-from-favourites`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    return {
      ok: false,
      data: e,
    };
  }
}

/**
 * Function for fetching data for FAVOURITES table based on desired params.
 *
 * @param params
 */
export async function fetchMyFavourites(
  params: Params
): Promise<Result<FavouriteArchivalEntity>> {
  const response = await fetch(`/api/favourite/my-favourites`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(params),
  });
  const json = await response.json();

  return json;
}

/**
 * Hook that returns entity type for table.
 *
 * USAGE: in tableCellEnumFactory cell component (favourites.tsx).
 */
export function useArchivalEntityTypes() {
  return [
    {
      id: ArchivalEntityType.ARCHIVAL_AID,
      label: 'Archivní pomůcka',
    },
    {
      id: ArchivalEntityType.ARCHIVAL_COLLECTION,
      label: 'Archivní fond',
    },
    {
      id: ArchivalEntityType.ARCHIVAL_UNIT,
      label: 'Archiválie',
    },
    {
      id: ArchivalEntityType.EAD_NODE,
      label: 'Uzel EAD',
    },
  ];
}
